/**
 * Frontend TypeScript Types
 * Matches backend API responses
 */

// ==================== ENUMS ====================

export type TierStatus = 'COLLECTING' | 'PENDING_ACTIVATION' | 'ACTIVE_MINING' | 'ARCHIVE';

export enum PayoutSchedule {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  END_OF_TERM = 'END_OF_TERM',
}

export enum InvestmentStatus {
  PENDING = 'PENDING',
  PENDING_USDT = 'PENDING_USDT',     // Step 1: Waiting for USDT payment (Phantom)
  PENDING_VERIFICATION = 'PENDING_VERIFICATION', // Step 1b: Transaction submitted, verifying on chain
  PENDING_TOKENS = 'PENDING_TOKENS', // Step 2: USDT paid, waiting for LAIKA/TAKARA (Phantom)
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  WITHDRAWN = 'WITHDRAWN',
  SOLD = 'SOLD',
  CANCELLED = 'CANCELLED',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED', // Auto-verification failed, manual check needed
}

export enum TokenType {
  USDT = 'USDT',
  TAKARA = 'TAKARA',
  LAIKA = 'LAIKA',
}

// ==================== TIER TYPES (v4.0) ====================

export interface Tier {
  id: string
  number: number
  name: string
  status: TierStatus
  miningThreshold: number
  currentFilled: number
  activationDelayMinutes: number
  activationDate?: string
  isActive: boolean
  vaultCount: number
  activeInvestments: number
  // Countdown
  countdownSeconds?: number
  isCountdownActive?: boolean
  // Vaults in this tier
  vaults?: Vault[]
  createdAt: string
  updatedAt: string
}

// ==================== VAULT TYPES ====================

export interface Vault {
  id: string
  name: string
  duration: number // months
  payoutSchedule: PayoutSchedule
  minInvestment: number // Per-vault minimum
  baseAPY: number
  maxAPY: number
  baseTakaraAPY: number // Base TAKARA mining APY
  maxTakaraAPY: number // Max TAKARA mining APY with boost
  dynamicTakaraAPY?: number // v3.0: Dynamic TAKARA APY calculated from TVL
  requireTAKARA: boolean
  takaraRatio?: number
  isActive?: boolean // True = accepting investments, false = closed
  acceptedPayments?: string // Comma-separated: USDT,TAKARA
  activeInvestments: number
  isComingSoon?: boolean // v2.8: True for placeholder vaults
  comingSoonText?: string // v2.8: Text to display for coming soon badge
  // v5.0: Current tier info (shared across all vaults)
  currentTier?: {
    id: string
    name: string
    number: number
    status: string
    currentFilled: number
    miningThreshold: number
    activationDate?: string
    countdownSeconds?: number
    isCountdownActive?: boolean
  }
}

export interface VaultWithStats extends Vault {
  stats?: {
    averageAPY: number
    totalTakaraMined: number
    recentInvestments: any[]
  }
}

// ==================== INVESTMENT TYPES ====================

export interface Investment {
  id: string
  vaultName: string
  vaultTier: string
  usdtAmount: number
  takaraLocked: number
  finalAPY: number
  startDate: string
  endDate: string
  status: InvestmentStatus
  totalEarnedUSDT: number
  totalMinedTAKARA: number
  pendingUSDT: number
  pendingTAKARA: number
  nftMintAddress?: string
  // v3.3: NFT minting flags
  nftReadyToMint?: boolean
  isNFTMinted?: boolean
  laikaBoost?: {
    laikaAmount: number
    additionalAPY: number
    isReturned: boolean
  }
  // v2.2: TAKARA boost support
  takaraBoost?: {
    takaraAmount: number
    takaraValueUSD: number
    maxAllowedUSD: number
    boostPercentage: number
    additionalAPY: number
    isReturned: boolean
  }
  // v2.2: Instant sale support
  instantSalePrice?: number
  isInstantSaleEnabled: boolean
  lastMiningDate?: string
  // v3.5: NFT ownership indicator
  isOriginalOwner?: boolean // true if user created investment, false if purchased NFT
  // Contract claim fields
  availableRewardUSDT: number
  availableRewardTAKARA: number
  referralDeductionUSDT: number
  referralDeductionTAKARA: number
  // Claim lock info (active SIGNED claim pending on-chain submission)
  claimLockedUntil?: {
    REWARD_USDT?: string    // ISO date when lock expires
    REWARD_TAKARA?: string
  }
  // Mint lock info (active Redis mint session)
  mintLockedUntil?: string  // ISO date when mint lock expires
}

export interface InvestmentCalculation {
  vault: {
    id: string
    name: string
    duration: number
  }
  investment: {
    usdtAmount: number
    requiredTAKARA: number
    laikaBoostUSD: number
    laikaToUsdtRate?: number
    laikaValueUSD?: number
    // v2.2 - LAIKA price and discount fields
    laikaAmount?: number
    laikaPrice?: number
    laikaMarketValueUSD?: number
    laikaDiscountPercent?: number
    laikaDiscountAmount?: number
    laikaDiscountedValueUSD?: number
    // v2.9 - x2 LAIKA boost fields
    laikaBoostValueUSD?: number
    laikaPremiumPercent?: number
  }
  laika?: {
    // LAIKA boost details
    maxBoostValue?: number
    currentBoostPercent?: number
  }
  earnings: {
    baseAPY: number
    laikaBoostAPY: number
    finalAPY: number
    totalUSDT: number
    monthlyUSDT: number
    payoutSchedule: PayoutSchedule
    numberOfPayouts: number
    payoutAmount: number
  }
  mining: {
    baseTakaraAPY: number // Base TAKARA mining APY
    maxTakaraAPY: number // Max TAKARA mining APY
    currentDifficulty: number
    dailyTAKARA: number
    monthlyTAKARA: number
    totalTAKARA: number
  }
  summary: {
    totalInvestment: number
    totalUSDTReturn: number
    totalTAKARAMined: number
    roi: string
  }
}

// ==================== MARKETPLACE TYPES ====================

export interface MarketplaceListing {
  id: string
  investmentId: string
  priceUSDT: number
  originalInvestment: number
  currentValue: number
  vault: {
    id: string
    name: string
    tierName: string  // From investment.tier, not vault
    duration: number
  }
  finalAPY: number
  remainingMonths: number
  totalEarnedUSDT: number
  totalMinedTAKARA: number
  laikaBoost?: {
    laikaAmount: number
    additionalAPY: number
  }
  seller: {
    walletAddress: string
    username?: string
  }
  nftMintAddress?: string
  createdAt: string
  platformFee: number
}

// ==================== USER TYPES ====================

export interface User {
  id: string
  walletAddress?: string       // Solana (Phantom) - for all operations
  username?: string
  email?: string
  totalInvested: number
  totalEarnedUSDT: number
  totalMinedTAKARA: number
  createdAt: string
  lastLoginAt?: string
  // Referral fields (v2.8)
  ownReferralCode?: string
  canInvite?: boolean
  referralLevel?: number       // 1=L1, 2=L2, 3=L3 (null if no referrer)
  referralWalletAddress?: string // Dedicated wallet address for receiving referral bonuses
  // Contract referral claim fields
  availableReferralUSDT?: number
  availableReferralTAKARA?: number
  // Claim lock info (active SIGNED claim pending on-chain submission)
  claimLockedUntil?: {
    REFERRAL_USDT?: string    // ISO date when lock expires
    REFERRAL_TAKARA?: string
  }
}

// ==================== CLAIM REQUEST TYPES (v2.2) ====================

export enum ClaimType {
  USDT = 'USDT',
  TAKARA = 'TAKARA',
}

export enum ClaimRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED',
}

export interface ClaimRequest {
  id: string
  claimType: ClaimType
  amount: number
  taxAmount: number
  amountAfterTax: number
  status: ClaimRequestStatus
  destinationWallet: string
  txSignature?: string
  rejectionReason?: string
  processedAt?: string
  createdAt: string
  user: {
    id: string
    username?: string
    walletAddress?: string
    email?: string
  }
  investment: {
    id: string
    usdtAmount: number
    status: InvestmentStatus
    vaultName: string
    vaultTier: string
  }
}

// ==================== API RESPONSE TYPES ====================

export interface ApiResponse<T = any> {
  success: boolean
  errorCode?: string
  message?: string
  data?: T
  error?: string
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// ==================== AUTH TYPES ====================

// v3.0: Partner stats for unified login
export interface PartnerStats {
  totalEarnedUSDT: number
  totalEarnedTAKARA: number
  pendingUSDT: number
  pendingTAKARA: number
  totalL1Users: number
  totalL2Users: number
  totalL3Users: number
}

// v3.0: Partner data for unified login
export interface PartnerData {
  id: string
  name: string
  referralCode: string
  isActive: boolean
  stats: PartnerStats
}

// v3.1: Partner profile for referrer dashboard (includes linkedUser)
export interface PartnerProfile {
  id: string
  name: string
  email: string
  referralCode: string
  isActive: boolean
  totalL1Users: number
  totalL2Users: number
  totalL3Users: number
  totalInvestments: number
  totalEarnedUSDT: number
  totalEarnedTAKARA: number
  pendingUSDT: number
  pendingTAKARA: number
  createdAt: string
  linkedUserId?: string
  linkedUser?: {
    id: string
    walletAddress: string
  }
}

export interface LoginResponse {
  success: boolean
  token: string
  user: User
  // v3.0: Partner info for unified Phantom wallet login
  isPartner?: boolean
  partnerData?: PartnerData
}

export interface NonceResponse {
  success: boolean
  data: {
    nonce: string
    message: string
    expiresAt: string
  }
}

// ==================== DASHBOARD TYPES ====================

export interface DashboardStats {
  totalInvestments: number
  totalValueLocked: number
  totalEarnedUSDT: number
  totalMinedTAKARA: number
  activeInvestments: number
}

// ==================== FORM TYPES ====================

export type PaymentMethod = 'TAKARA' | 'USDT'

export interface CreateInvestmentInput {
  vaultId: string
  usdtAmount: number
  takaraAmount?: number
  takaraBoost?: {
    takaraAmount: number
    takaraValueUSD: number
    txSignature?: string // v3.0: Separate signature for TAKARA boost transaction
  }
  laikaBoost?: {
    laikaAmount: number
    laikaValueUSD: number
    txSignature?: string // v3.0: Separate signature for LAIKA boost transaction
  }
  txSignature: string // USDT deposit transaction signature
  takaraRequiredTxSignature?: string // v3.0: Separate signature for required TAKARA transaction
  paymentMethod?: PaymentMethod  // 'TAKARA' or 'USDT'
}

export interface CalculateInvestmentInput {
  usdtAmount: number
  laikaBoostUSD?: number
  laikaAmount?: number
  takaraAmount?: number  // TAKARA boost amount (v2.9)
}
